import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PerformanceColumn, PerformanceSectionProps, PerformanceStatus } from './PerformanceSection.types';
import { EmailReminderType, ReminderJourneyOverride, ReminderJourneys, SentJourneyLog } from 'generated/event.types';

const getHoursDiff = (targetDate?: Date | string): number | null => {
  if (!targetDate) {
    return null;
  }
  const date = new Date(targetDate);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  return diffMs / (1000 * 60 * 60);
};

const getJourneyStatus = (
  journeyName: string,
  hoursUntilEventStart: number | null,
  hoursUntilEventEnd: number | null,
  logs?: SentJourneyLog[],
  timeInHour?: number | null,
  format?: EmailReminderType | null,
): PerformanceStatus => {
  const journeyLogs = logs?.filter((l) => l.journeyName === journeyName) || [];

  const hasSuccess = journeyLogs.some((l) => l.status === PerformanceStatus.success);
  if (hasSuccess) {
    return PerformanceStatus.success;
  }

  const hasFailed = journeyLogs.some((l) => l.status === PerformanceStatus.failed);
  if (hasFailed) {
    return PerformanceStatus.failed;
  }

  if (timeInHour !== undefined && timeInHour !== null) {
    if (format === EmailReminderType.after_end) {
      if (hoursUntilEventEnd !== null && hoursUntilEventEnd > -timeInHour) {
        return PerformanceStatus.pending;
      }
    } else if (hoursUntilEventStart !== null && hoursUntilEventStart > timeInHour) {
      return PerformanceStatus.pending;
    }
  }

  return PerformanceStatus.na;
};

const isJourneyEnabled = (
  journey: ReminderJourneys,
  eventMarket?: string,
  overridesMap?: Map<string, boolean>,
): boolean => {
  const overrideEnabled = overridesMap?.get(journey._id);
  if (overrideEnabled !== undefined) {
    return overrideEnabled;
  }

  if (!eventMarket || !journey.marketDefaults) {
    return false;
  }
  return journey.marketDefaults.includes(eventMarket as never);
};

const toCamelCaseKey = (name: string): string => {
  return name
    .split(' ')
    .map((word, index) => (index === 0
      ? word.toLowerCase()
      : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
    .join('');
};

const createOverridesMap = (overrides?: ReminderJourneyOverride[]): Map<string, boolean> => {
  const map = new Map<string, boolean>();
  if (overrides) {
    overrides.forEach((override) => {
      map.set(override.journeyId, override.enabled);
    });
  }
  return map;
};

const usePerformanceSection = (props: PerformanceSectionProps) => {
  const { t } = useTranslation();
  const {
    sentJourneyLogs,
    attendedZoom,
    attendedZoomDuration,
    registrationEmailSent,
    eventStartDate,
    eventEndDate,
    eventMarket,
    journeys,
    reminderOverrides,
  } = props;

  const columns: PerformanceColumn[] = useMemo(() => {
    const hoursUntilEventStart = getHoursDiff(eventStartDate);
    const hoursUntilEventEnd = getHoursDiff(eventEndDate);
    const overridesMap = createOverridesMap(reminderOverrides);
    const result: PerformanceColumn[] = [];

    const registrationStatus = registrationEmailSent
      ? PerformanceStatus.success
      : PerformanceStatus.na;
    result.push({
      key: 'registration',
      label: t('performance_column_registration'),
      status: registrationStatus,
    });

    const filteredJourneys = journeys
      ?.filter((journey) => journey.active && isJourneyEnabled(journey, eventMarket, overridesMap))
      .sort((a, b) => {
        if (a.format === EmailReminderType.before_start && b.format === EmailReminderType.after_end) {
          return -1;
        }
        if (a.format === EmailReminderType.after_end && b.format === EmailReminderType.before_start) {
          return 1;
        }
        return (b.timeInHour || 0) - (a.timeInHour || 0);
      }) || [];

    filteredJourneys.forEach((journey) => {
      const status = getJourneyStatus(
        journey.name,
        hoursUntilEventStart,
        hoursUntilEventEnd,
        sentJourneyLogs,
        journey.timeInHour,
        journey.format,
      );
      result.push({
        key: toCamelCaseKey(journey.name),
        label: journey.name,
        status,
      });
    });

    const attendedZoomStatus = attendedZoom === undefined
      ? PerformanceStatus.na
      : attendedZoom
        ? PerformanceStatus.success
        : PerformanceStatus.failed;
    result.push({
      key: 'attendedZoom',
      label: t('performance_column_attended_zoom'),
      status: attendedZoomStatus,
      useYesNo: true,
    });

    const zoomDurationStatus = attendedZoomDuration === undefined || attendedZoomDuration === null
      ? PerformanceStatus.na
      : PerformanceStatus.success;
    const zoomDurationValue = attendedZoomDuration
      ? `${Math.ceil(attendedZoomDuration / 60)} ${t('short_minutes_label')}`
      : undefined;
    result.push({
      key: 'zoomDuration',
      label: t('performance_column_zoom_duration'),
      status: zoomDurationStatus,
      customValue: zoomDurationValue,
    });

    return result;
  }, [
    sentJourneyLogs, registrationEmailSent, attendedZoom, attendedZoomDuration,
    eventStartDate, eventEndDate, eventMarket, journeys, reminderOverrides, t,
  ]);

  const getStatusLabel = (status: PerformanceStatus, useYesNo?: boolean): string => {
    switch (status) {
      case PerformanceStatus.success:
        return useYesNo ? t('common_yes') : t('performance_status_success');
      case PerformanceStatus.failed:
        return useYesNo ? t('common_no') : t('performance_status_fail');
      case PerformanceStatus.pending:
        return t('performance_status_pending');
      case PerformanceStatus.na:
      default:
        return t('performance_status_na');
    }
  };

  return {
    columns,
    getStatusLabel,
    t,
  };
};

export default usePerformanceSection;
